import { useMemo } from 'react';

export type FeedEntryType = 'story' | 'milestone' | 'press' | 'highlight';

export interface ProfileFeedEntry {
  id: string;
  type: FeedEntryType;
  title: string;
  body?: string | null;
  url?: string | null;
  image_url?: string | null;
  tag?: string | null;
  published_at?: string | null;
}

interface ProfileFeedEditorProps {
  entries: ProfileFeedEntry[];
  onChange: (entries: ProfileFeedEntry[]) => void;
}

function createEntry(type: FeedEntryType): ProfileFeedEntry {
  const id = typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `feed-${Date.now()}`;
  return { id, type, title: '', body: '', url: '', image_url: '', tag: type === 'milestone' ? 'Milestone' : '' };
}

function updateEntry(entries: ProfileFeedEntry[], id: string, updates: Partial<ProfileFeedEntry>) {
  return entries.map((e) => (e.id === id ? { ...e, ...updates } : e));
}

const FEED_TYPES: { value: FeedEntryType; label: string }[] = [
  { value: 'story', label: 'Story update' },
  { value: 'milestone', label: 'Milestone' },
  { value: 'highlight', label: 'Highlight' },
  { value: 'press', label: 'Press mention' },
];

export default function ProfileFeedEditor({ entries, onChange }: ProfileFeedEditorProps) {
  const list = useMemo(() => entries || [], [entries]);

  return (
    <div className="space-y-3">
      <header className="space-y-1">
        <h3 className="text-lg font-semibold text-cocoa">Profile feed</h3>
        <p className="text-sm text-cocoa/70">Updates appear on your public profile in chronological order.</p>
      </header>
      <div className="space-y-3">
        {list.map((entry) => (
          <div key={entry.id} className="space-y-3 rounded-2xl border border-sand/70 bg-white/70 p-4">
            <div className="grid gap-3 sm:grid-cols-3">
              <label className="text-sm font-medium text-cocoa">
                Type
                <select
                  className="mt-1 w-full rounded-xl border border-sand px-3 py-2 text-sm text-cocoa focus:border-cocoa focus:outline-none"
                  value={entry.type}
                  onChange={(e) => onChange(updateEntry(list, entry.id, { type: e.target.value as FeedEntryType }))}
                >
                  {FEED_TYPES.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </label>
              <label className="sm:col-span-2 text-sm font-medium text-cocoa">
                Title
                <input
                  className="mt-1 w-full rounded-xl border border-sand px-3 py-2 text-sm text-cocoa focus:border-cocoa focus:outline-none"
                  value={entry.title}
                  onChange={(e) => onChange(updateEntry(list, entry.id, { title: e.target.value }))}
                  placeholder="New achievement unlocked!"
                />
              </label>
            </div>
            <label className="text-sm font-medium text-cocoa">
              Body
              <textarea
                className="mt-1 w-full rounded-xl border border-sand px-3 py-2 text-sm text-cocoa focus:border-cocoa focus:outline-none"
                rows={3}
                value={entry.body || ''}
                onChange={(e) => onChange(updateEntry(list, entry.id, { body: e.target.value }))}
                placeholder="Share details about this update."
              />
            </label>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="text-sm font-medium text-cocoa">
                Image URL (optional)
                <input
                  className="mt-1 w-full rounded-xl border border-sand px-3 py-2 text-sm text-cocoa focus:border-cocoa focus:outline-none"
                  value={entry.image_url || ''}
                  onChange={(e) => onChange(updateEntry(list, entry.id, { image_url: e.target.value }))}
                />
              </label>
              <label className="text-sm font-medium text-cocoa">
                Tag / Pill
                <input
                  className="mt-1 w-full rounded-xl border border-sand px-3 py-2 text-sm text-cocoa focus:border-cocoa focus:outline-none"
                  value={entry.tag || ''}
                  onChange={(e) => onChange(updateEntry(list, entry.id, { tag: e.target.value }))}
                  placeholder="Milestone"
                />
              </label>
            </div>
            <div className="flex justify-end">
              <button type="button" className="text-sm font-medium text-brick" onClick={() => onChange(list.filter((item) => item.id !== entry.id))}>
                Remove
              </button>
            </div>
          </div>
        ))}
      </div>
      <button type="button" className="rounded-xl border border-cocoa px-4 py-2 text-sm font-semibold text-cocoa" onClick={() => onChange([...list, createEntry('story')])}>
        Add feed entry
      </button>
    </div>
  );
}
